// httpClient autonome (n'importe pas react-admin pour rester testable en Jest).

export const TOKEN_KEY = "ioum_token";
export const IDENTITY_KEY = "ioum_identity";

// Jeton d'administration : JWT émis par le backend (POST /admin/auth/login,
// claim adm=true, 8 h). Gardé en mémoire ET en sessionStorage (limité à
// l'onglet, effacé à sa fermeture) pour qu'un rechargement de page ne
// déconnecte pas l'admin. Jamais en localStorage (persistance durable,
// partagée entre onglets).
let memoryToken = null;

// Purge des anciennes versions qui stockaient le token en localStorage.
try {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(IDENTITY_KEY);
} catch (_) {
  // stockage indisponible (navigation privée…) : rien à purger
}

export const getToken = () => {
  if (memoryToken) return memoryToken;
  try {
    memoryToken = sessionStorage.getItem(TOKEN_KEY) || null;
  } catch (_) {
    memoryToken = null;
  }
  return memoryToken;
};

export const setToken = (token) => {
  memoryToken = token || null;
  try {
    if (memoryToken) sessionStorage.setItem(TOKEN_KEY, memoryToken);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
};

export const clearToken = () => {
  memoryToken = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
};

// Crochet optionnel exécuté avant chaque appel (historique : rafraîchissement
// du token Firebase). Le jeton admin backend n'est pas rafraîchi : à
// expiration (8 h) le backend répond 401 et authProvider renvoie au login.
let tokenRefresher = null;

export const setTokenRefresher = (fn) => {
  tokenRefresher = typeof fn === "function" ? fn : null;
};

export const refreshToken = async () => {
  if (!tokenRefresher) return;
  try {
    const fresh = await tokenRefresher();
    if (fresh) setToken(fresh);
  } catch (_) {
    // échec : on garde le token courant
  }
};

// Headers communs à tous les appels BO :
//  - X-React-Admin : marque la requête comme venant du BO côté backend
//  - Authorization : jeton admin backend (le backend exige un token sur tout
//    endpoint non listé comme public, cf. SecurityConfig.anyRequest())
// Ces deux headers sont autorisés côté CORS backend (allowedHeaders).
export const buildHeaders = (extra) => {
  const headers = new Headers(extra || {});
  headers.set("X-React-Admin", "true");
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const token = getToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};

// Émule fetchUtils.fetchJson : retourne { status, headers, body, json }
// et lève une erreur (avec status) sur HTTP non-OK. Permet de ne pas dépendre
// de react-admin dans les tests.
export const httpClient = async (url, options = {}) => {
  await refreshToken();
  const headers = buildHeaders(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  } else if (options.body instanceof FormData) {
    headers.delete("Content-Type");
  }
  const res = await fetch(url, { ...options, headers });
  const text = await res.text();
  let json;
  try {
    json = text ? JSON.parse(text) : null;
  } catch (_) {
    json = null;
  }
  if (!res.ok) {
    const err = new Error(
      (json && (json.message || json.error)) ||
        (text && text.length < 300 ? text : null) ||
        res.statusText ||
        "API Error"
    );
    err.status = res.status;
    err.body = text;
    err.json = json;
    throw err;
  }
  return { status: res.status, headers: res.headers, body: text, json };
};

export const rawFetch = async (url, options = {}) => {
  await refreshToken();
  const headers = buildHeaders(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  } else if (options.body instanceof FormData) {
    headers.delete("Content-Type");
  }
  const res = await fetch(url, { ...options, headers });
  return res;
};

export const isFileValue = (v) =>
  v && typeof v === "object" && (v.rawFile instanceof File || v instanceof File);

export const collectFiles = (data) => {
  const files = [];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (isFileValue(value)) {
      files.push({ key, file: value.rawFile || value });
    } else if (Array.isArray(value) && value.some(isFileValue)) {
      value.forEach((v) => {
        if (isFileValue(v)) files.push({ key, file: v.rawFile || v });
      });
    }
  });
  return files;
};

export const buildFormData = (data) => {
  const fd = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (isFileValue(value)) {
      fd.append(key, value.rawFile || value);
    } else if (Array.isArray(value) && value.some(isFileValue)) {
      value.forEach((v) => {
        if (isFileValue(v)) fd.append(key, v.rawFile || v);
      });
    } else if (value != null && typeof value === "object") {
      fd.append(key, JSON.stringify(value));
    } else if (value != null) {
      fd.append(key, value);
    }
  });
  return fd;
};
